import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PhoneIcon from "@mui/icons-material/Phone";
import CallIcon from "@mui/icons-material/Call";
import SendIcon from "@mui/icons-material/Send";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LogoutIcon from "@mui/icons-material/Logout";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SecurityIcon from "@mui/icons-material/Security";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";

const decodeToken = (token) => {
  try {
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return { email: payload.email || 'Unknown User', userId: payload.userId || null, role: payload.role || 'user' };
  } catch { return null; }
};

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED = 72;

const SidebarRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "collapsed",
})(({ collapsed }) => ({
  width: collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH,
  minWidth: collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH,
  height: "100vh",
  background: "#0f172a",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  zIndex: 100,
  borderRight: "1px solid rgba(255,255,255,0.06)",
  overflow: "hidden",
}));

const LogoArea = styled(Box)({
  padding: "24px 20px 16px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  cursor: "pointer",
  flexShrink: 0,
});

const NavSection = styled(Box)({
  flex: 1,
  padding: "8px 12px",
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  overflowY: "auto",
  overflowX: "hidden",
});

const NavItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "collapsed",
})(({ active, collapsed }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: collapsed ? "12px" : "10px 16px",
  justifyContent: collapsed ? "center" : "flex-start",
  borderRadius: "10px",
  cursor: "pointer",
  color: active ? "#f8fafc" : "#94a3b8",
  background: active ? "rgba(99, 102, 241, 0.15)" : "transparent",
  transition: "all 0.15s ease",
  whiteSpace: "nowrap",
  position: "relative",
  "&:hover": {
    background: active ? "rgba(99, 102, 241, 0.2)" : "#1e293b",
    color: "#f8fafc",
  },
  ...(active && {
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: "3px",
      height: "24px",
      borderRadius: "99px",
      background: "#6366f1",
    },
  }),
}));

const NavLabel = styled(Typography)({
  fontSize: "0.7rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#475569",
  padding: "20px 16px 8px",
  whiteSpace: "nowrap",
});

const BottomSection = styled(Box)({
  padding: "12px",
  borderTop: "1px solid rgba(255,255,255,0.06)",
  flexShrink: 0,
});

const UserCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "collapsed",
})(({ collapsed }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: collapsed ? "8px" : "10px 12px",
  justifyContent: collapsed ? "center" : "flex-start",
  borderRadius: "10px",
  background: "#1e293b",
  transition: "all 0.15s ease",
  overflow: "hidden",
}));

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

export default function Navigation({ userEmail }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [liveModules, setLiveModules] = useState(null);

  const email = userEmail || localStorage.getItem("email") || decodeToken(localStorage.getItem("token"))?.email || "Guest";
  const tokenData = decodeToken(localStorage.getItem("token"));
  const userRole = tokenData?.role || "user";

  // Fetch live user profile so modules stay current even if JWT was issued before assignment
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API}/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data?.modules) setLiveModules(data.modules); })
      .catch(() => {});
  }, []);

  const userModules = (liveModules || tokenData?.modules || "ivr").split(",");

  const navItems = [
    { path: "/", icon: CallIcon, label: "Execute Flow", module: "ivr" },
    { path: "/create-flow", icon: AutoFixHighIcon, label: "AI Builder", module: "ivr" },
    { path: "/call-logs", icon: PhoneIcon, label: "Call Logs", module: null },
    { path: "/ivr-dashboard", icon: DashboardIcon, label: "IVR Dashboard", module: "ivr" },
    { path: "/ai-voice", icon: RecordVoiceOverIcon, label: "AI Voice", module: "ai_voice" },
    { path: "/sms", icon: SendIcon, label: "SMS", module: null },
    ...(userRole === "admin" ? [{ path: "/admin", icon: AdminPanelSettingsIcon, label: "Admin Panel", module: null }] : []),
  ].filter((item) => !item.module || userModules.includes(item.module));

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    navigate("/login");
    window.location.reload();
  };

  const getInitials = (emailStr) =>
    emailStr ? emailStr.split("@")[0].substring(0, 2).toUpperCase() : "U";

  return (
    <SidebarRoot collapsed={collapsed}>
      {/* Logo */}
      <LogoArea onClick={() => navigate("/")}>
        <Box
          sx={{
            width: 36,
            height: 36,
            minWidth: 36,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SecurityIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        {!collapsed && (
          <Box>
            <Typography sx={{ color: "#f8fafc", fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.2, letterSpacing: "-0.01em" }}>
              VishTrap
            </Typography>
            <Typography sx={{ color: "#475569", fontSize: "0.65rem", fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Security Platform
            </Typography>
          </Box>
        )}
      </LogoArea>

      {/* Navigation */}
      <NavSection>
        {!collapsed && <NavLabel>Main Menu</NavLabel>}
        {navItems.map((item) => (
          <Tooltip key={item.path} title={collapsed ? item.label : ""} placement="right" arrow>
            <NavItem active={isActive(item.path)} collapsed={collapsed} onClick={() => navigate(item.path)}>
              <item.icon sx={{ fontSize: 20 }} />
              {!collapsed && (
                <Typography sx={{ fontSize: "0.875rem", fontWeight: isActive(item.path) ? 600 : 500 }}>
                  {item.label}
                </Typography>
              )}
            </NavItem>
          </Tooltip>
        ))}
      </NavSection>

      {/* Collapse toggle */}
      <Box sx={{ px: "12px", mb: 1 }}>
        <NavItem collapsed={collapsed} onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? (
            <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
          ) : (
            <>
              <KeyboardArrowLeftIcon sx={{ fontSize: 20 }} />
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>Collapse</Typography>
            </>
          )}
        </NavItem>
      </Box>

      {/* User section */}
      <BottomSection>
        <UserCard collapsed={collapsed}>
          <Avatar
            sx={{
              width: 32, height: 32, fontSize: "0.75rem", fontWeight: 700,
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
            }}
          >
            {getInitials(email)}
          </Avatar>
          {!collapsed && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: "#f8fafc", fontSize: "0.8rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {email.split("@")[0]}
              </Typography>
              <Typography sx={{ color: "#475569", fontSize: "0.7rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {email}
              </Typography>
            </Box>
          )}
          <Tooltip title="Logout" placement="right" arrow>
            <IconButton
              onClick={handleLogout}
              size="small"
              sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444", background: "rgba(239,68,68,0.1)" }, ml: collapsed ? 0 : "auto" }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </UserCard>
      </BottomSection>
    </SidebarRoot>
  );
}
